import { theme } from '@app/styles/theme'
import { css } from '@emotion/react'

export const tableWrapper = css`
  width: 100%;
  display: flex;
  flex-direction: column;
`

export const toolbar = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
`

export const toolbarLeft = css`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
`

export const searchInput = css`
  width: 220px;

  .MuiInputBase-root {
    height: 34px;
    font-size: 0.875rem;
  }

  .MuiInputBase-input {
    padding-top: 6px;
    padding-bottom: 6px;
  }
`

export const categoryFilter = css`
  width: 170px;

  .MuiInputBase-root {
    height: 34px;
    font-size: 0.875rem;
  }

  .MuiSelect-select {
    display: flex;
    align-items: center;
    min-height: auto;
    padding-top: 6px;
    padding-bottom: 6px;
  }
`

export const toolbarRight = css`
  margin-left: auto;
  display: flex;
  align-items: center;
`

export const compactTextButton = css`
  min-width: auto;
  padding: 4px 6px;
  font-size: 0.75rem;
  line-height: 1.2;
`

export const compactOutlinedButton = css`
  min-width: auto;
  padding: 4px 8px;
  font-size: 0.8125rem;
  line-height: 1.2;

  .MuiButton-startIcon {
    margin-right: 4px;
  }
`

export const tableContainer = css`
  overflow-x: auto;
  overflow-y: visible;
`

export const headerCell = css`
  min-width: 120px;
  white-space: nowrap;
  background-color: ${theme.palette.primary['100']};
  color: ${theme.palette.primary.main};
`

export const useCaseStaticHeaderCell = css`
  border-bottom: none;
  background-color: ${theme.palette.neutral.light};
`

export const useCaseGroupHeaderCell = css`
  font-weight: 500;
  font-size: 12px;
  color: ${theme.palette.secondary.main};
  border-bottom: 2px solid ${theme.palette.secondary.main};
  background-color: ${theme.palette.neutral.light};
`

export const useCaseTrailingHeaderCell = css`
  z-index: 4;
  border-bottom: none;
  background-color: ${theme.palette.neutral.light};
`

export const footerBox = css`
  padding: 4px 16px;
  border-top: 1px solid ${theme.palette.neutral.light};
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const bodyCell = css`
  a {
    max-width: 100%;
  }
`

export const emptyRow = css`
  padding: 48px 0;
  text-align: center;
`

const FROZEN_WIDTH = 350

export const stickyLeft = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 3,
  backgroundColor: theme.palette.neutral.light,
  borderRight: '1px solid',
  borderBottom: 'none',
  borderColor: theme.palette.neutral[500],
  minWidth: FROZEN_WIDTH,
  maxWidth: FROZEN_WIDTH,
}

export const stickyLeftHeader = {
  ...stickyLeft,
  zIndex: 4,
  backgroundColor: theme.palette.neutral.light,
}

export const stickyLeftBody = {
  ...stickyLeft,
  zIndex: 2,
}

export const lastRowNoBorder = {
  '&:last-child td': {
    borderBottom: 0,
  },
}

export const tableStyles = css`
  border-collapse: separate;
  border-spacing: 0;

  .MuiTableCell-root {
    border-bottom: 1px solid ${theme.palette.neutral.light};
  }

  .MuiTableHead-root .MuiTableCell-root {
    border-top: none;
    border-bottom: 1px solid ${theme.palette.neutral.light};
    border-top: 1px solid ${theme.palette.neutral.light};
  }

  .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root {
    border-bottom: none;
  }

  .MuiTableCell-root[data-frozen='left'] {
    border-right: 1px solid ${theme.palette.neutral.light};
  }

  .MuiTableCell-root[data-frozen='right'] {
    border-left: 1px solid ${theme.palette.neutral.light};
  }

  .MuiTableRow-root:hover .MuiTableCell-root[data-frozen='left'],
  .MuiTableRow-root:hover .MuiTableCell-root[data-frozen='right'] {
    background-color: ${theme.palette.action.hover};
  }
`

export const tableMinWidth = css`
  min-width: max-content;
`

export const stickyLeftHeaderCss = css`
  position: sticky;
  left: 0;
  z-index: 4;
  min-width: 350px;
  max-width: 350px;
`

export const stickyLeftBodyCss = css`
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: ${theme.palette.neutral[900]};
  min-width: 350px;
  max-width: 350px;
  box-shadow: inset -1px 0 0 ${theme.palette.neutral[500]};
`

export const groupHeaderLeftCell = css`
  position: sticky;
  left: 0;
  z-index: 4;
  background-color: ${theme.palette.neutral.light};
`

export const groupHeaderDetailsCell = css`
  background-color: ${theme.palette.neutral.light};
  color: ${theme.palette.text.secondary};
  border-bottom: 2px solid ${theme.palette.divider};
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
`

export const groupHeaderUseCasesCell = css`
  background-color: ${theme.palette.secondary['100']};
  color: ${theme.palette.secondary.main};
  border-bottom: 2px solid ${theme.palette.secondary.main};
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
`

export const headerCellBase = css`
  min-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  max-width: 200px;
  text-overflow: ellipsis;
`

export const headerCellDefault = css`
  &:first-child {
    background-color: ${theme.palette.neutral[900]};
  }
  background-color: ${theme.palette.neutral.light};
  color: ${theme.palette.text.primary};
`

export const headerCellUseCase = css`
  background-color: ${theme.palette.neutral.light};
  color: ${theme.palette.black.main};
`

export const modelNameCell = css`
  min-width: 0;
  padding-top: 6px;
  padding-bottom: 6px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  color: ${theme.palette.primary.main};
`
export const modelNameSubtitle = css`
  color: ${theme.palette.black.main};
  text-decoration: underline;
  cursor: pointer;
  white-space: wrap;
`

export const modelNameText = css`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
  color: ${theme.palette.primary.main};
  font-weight: 500;
`

export const caveatsText = css`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`
